import { useState, useRef, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Plus, Search, Download, Upload, FileText, Pencil, Trash2, Eye, X, Check, FileCode2, Paperclip } from "lucide-react";
import * as XLSX from "xlsx";

const STATUS_LABELS: Record<string, string> = {
  agendado: "Agendado",
  recebido: "Recebido",
  em_conferencia: "Em Conferência",
  conferido: "Conferido",
  finalizado: "Finalizado",
};

const STATUS_COLORS: Record<string, string> = {
  agendado: "bg-blue-100 text-blue-800",
  recebido: "bg-yellow-100 text-yellow-800",
  em_conferencia: "bg-purple-100 text-purple-800",
  conferido: "bg-purple-100 text-purple-800",
  finalizado: "bg-green-100 text-green-800",
};

function getFlagDataAgendamento(dataAgendamento: string | Date | undefined, status: string) {
  if (status !== "agendado" || !dataAgendamento) return null;
  const data = dataAgendamento instanceof Date ? dataAgendamento.toISOString().split("T")[0] : String(dataAgendamento).split("T")[0];
  const hoje = new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
  if (data < hoje) return { label: "Atrasado", color: "bg-red-100 text-red-800" };
  if (data === hoje) return { label: "Hoje", color: "bg-yellow-100 text-yellow-800" };
  return null;
}

interface ItemForm {
  produtoCod: string;
  produtoDescricao: string;
  codigoEntrada: string;
  retrabalhar: boolean;
  quantidade: string;
  unidade: string;
  custoUnitario: string;
  custoTotal: string;
  origemFiscal: string;
  codValido?: boolean | null; // null = não verificado, true = válido, false = inválido
  codEntradaValido?: boolean | null;
}

const emptyItem = (): ItemForm => ({
  produtoCod: "",
  produtoDescricao: "",
  codigoEntrada: "",
  retrabalhar: false,
  quantidade: "",
  unidade: "",
  custoUnitario: "",
  custoTotal: "",
  origemFiscal: "0",
  codValido: null,
  codEntradaValido: null,
});

const ITEMS_PER_PAGE = 20;

export default function AgendamentoEntradas() {
  const { user } = useGoogleAuth();
  const isCliente = user?.role === "cliente";
  const isAdminOrLider = user?.role === "admin" || user?.role === "lider";

  // Listagem
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("todos");
  const [filterDataAgendamento, setFilterDataAgendamento] = useState<"todos" | "atrasado" | "hoje">("todos");
  const [filterEmitenteId, setFilterEmitenteId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  // Modal criar/editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // Form states
  const [formNome, setFormNome] = useState("");
  const [formEmitenteId, setFormEmitenteId] = useState<number | null>(null);
  const [formEmitenteNome, setFormEmitenteNome] = useState("");
  const [formDataAgendamento, setFormDataAgendamento] = useState("");
  const [formObservacoes, setFormObservacoes] = useState("");
  const [formStatus, setFormStatus] = useState<string>("agendado");
  const [formItens, setFormItens] = useState<ItemForm[]>([emptyItem()]);
  const [upsellerConfigOpen, setUpsellerConfigOpen] = useState(false);
  const [upsellerEmitenteId, setUpsellerEmitenteId] = useState<number | null>(null);
  const [upsellerArmazem, setUpsellerArmazem] = useState("");
  const [upsellerAtivo, setUpsellerAtivo] = useState(true);
  const [upsellerLoginUrl, setUpsellerLoginUrl] = useState<string | null>(null);
  const [upsellerLoginEmitenteId, setUpsellerLoginEmitenteId] = useState<number | null>(null);
  const [upsellerAutenticadoEmitenteId, setUpsellerAutenticadoEmitenteId] = useState<number | null>(null);
  const [verificandoUpseller, setVerificandoUpseller] = useState(false);

  // Modal relatório
  const [isRelatorioOpen, setIsRelatorioOpen] = useState(false);
  const [relDataInicio, setRelDataInicio] = useState("");
  const [relDataFim, setRelDataFim] = useState("");
  const [relStatus, setRelStatus] = useState<string>("todos");

  const importRef = useRef<HTMLInputElement>(null);
  const modalImportRef = useRef<HTMLInputElement>(null);
  const [importandoItens, setImportandoItens] = useState(false);
  const [errosImportacaoItens, setErrosImportacaoItens] = useState<string[]>([]);
  const [validatingIndex, setValidatingIndex] = useState<number | null>(null);
  const [validatingCodEntradaIndex, setValidatingCodEntradaIndex] = useState<number | null>(null);

  // Loading ao abrir modal de editar/visualizar
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Modal XML/DANFE
  const [xmlModalEntradaId, setXmlModalEntradaId] = useState<number | null>(null);
  const [danfeModalEntradaId, setDanfeModalEntradaId] = useState<number | null>(null);

  // Modal Anexos
  const [anexosModalEntradaId, setAnexosModalEntradaId] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  // Queries
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !!user,
  });

  const { data: upsellerConfigs = [], refetch: refetchUpsellerConfigs } = trpc.upsellerEntrada.listarConfiguracoes.useQuery(undefined, {
    enabled: !!user,
  });

  const { data: entradasData, refetch } = trpc.entradas.list.useQuery(
    {
      emitenteId: filterEmitenteId ?? undefined,
      status: filterStatus !== "todos" ? filterStatus : undefined,
      filtroDataAgendamento: filterDataAgendamento !== "todos" ? filterDataAgendamento : undefined,
      search: searchTerm || undefined,
      page,
      pageSize: ITEMS_PER_PAGE,
    },
    { enabled: !!user }
  );

  const { data: relatorioData } = trpc.entradas.relatorio.useQuery(
    {
      emitenteId: isCliente ? (user?.emitenteId ?? undefined) : (filterEmitenteId ?? undefined),
      status: relStatus !== "todos" ? relStatus : undefined,
      dataInicio: relDataInicio || undefined,
      dataFim: relDataFim || undefined,
    },
    { enabled: isRelatorioOpen }
  );

  const entradas = entradasData?.data ?? [];
  const total = Number(entradasData?.total ?? 0);
  const totalPages = Math.max(1, Number(entradasData?.totalPages ?? 1));

  // Query XML da entrada
  const { data: xmlData, isLoading: xmlLoading, error: xmlError } = trpc.entradas.getXmlEntrada.useQuery(
    { entradaId: xmlModalEntradaId! },
    { enabled: xmlModalEntradaId !== null, retry: false }
  );

  // Query DANFE da entrada
  const { data: danfeData, isLoading: danfeLoading, error: danfeError } = trpc.entradas.getDanfeEntrada.useQuery(
    { entradaId: danfeModalEntradaId! },
    { enabled: danfeModalEntradaId !== null, retry: false }
  );

  // Query Anexos da entrada
  const { data: anexosData = [], refetch: refetchAnexos } = trpc.entradas.listAnexos.useQuery(
    { entradaId: anexosModalEntradaId! },
    { enabled: anexosModalEntradaId !== null }
  );


  // Mutations
  const createMutation = trpc.entradas.create.useMutation({
    onSuccess: () => {
      toast.success("Agendamento criado com sucesso!");
      handleCloseModal();
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const salvarUpsellerConfigMutation = trpc.upsellerEntrada.salvarConfiguracao.useMutation({
    onSuccess: () => {
      toast.success("Configuração do UpSeller salva.");
      refetchUpsellerConfigs();
      setUpsellerConfigOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.entradas.update.useMutation({
    onSuccess: () => {
      toast.success("Agendamento atualizado!");
      handleCloseModal();
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const updateItensMutation = trpc.entradas.updateItens.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.entradas.delete.useMutation({
    onSuccess: () => {
      toast.success("Agendamento excluído!");
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const saveAnexoMutation = trpc.entradas.saveAnexo.useMutation({
    onSuccess: () => { refetchAnexos(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteAnexoMutation = trpc.entradas.deleteAnexo.useMutation({
    onSuccess: () => { refetchAnexos(); toast.success("Anexo removido!"); },
    onError: (e) => toast.error(e.message),
  });

  // Validar código do produto ao pressionar Enter
  const handleValidateProdutoCod = async (index: number, cod: string) => {
    if (!cod.trim()) return;
    setValidatingIndex(index);
    try {
      const params = encodeURIComponent(JSON.stringify({ json: { cod: cod.trim().toUpperCase(), emitenteId: formEmitenteId ?? undefined } }));
      const res = await fetch(`/api/trpc/products.getByCod?input=${params}`, { credentials: "include" });
      const json = await res.json();
      const product = json?.result?.data?.json ?? null;
      setFormItens((prev) => {
        const next = [...prev];
        if (product) {
          next[index] = {
            ...next[index],
            produtoCod: cod.trim().toUpperCase(),
            produtoDescricao: product.descricao ?? "",
            codValido: true,
          };
          if (!next[index].retrabalhar) {
            next[index].codigoEntrada = cod.trim().toUpperCase();
          }
        } else {
          next[index] = { ...next[index], produtoDescricao: "", codValido: false };
          toast.error(`Código "${cod.trim()}" não encontrado nos produtos do emitente`);
        }
        return next;
      });
    } catch {
      toast.error("Erro ao validar código do produto");
    } finally {
      setValidatingIndex(null);
    }
  };

  // Validar código de entrada ao pressionar Enter (somente quando retrabalhar = true)
  const handleValidateCodEntrada = async (index: number, cod: string) => {
    if (!cod.trim()) return;
    setValidatingCodEntradaIndex(index);
    try {
      const params = encodeURIComponent(JSON.stringify({ json: { cod: cod.trim().toUpperCase(), emitenteId: formEmitenteId ?? undefined } }));
      const res = await fetch(`/api/trpc/products.getByCod?input=${params}`, { credentials: "include" });
      const json = await res.json();
      const product = json?.result?.data?.json ?? null;
      setFormItens((prev) => {
        const next = [...prev];
        if (product) {
          next[index] = {
            ...next[index],
            codigoEntrada: cod.trim().toUpperCase(),
            codEntradaValido: true,
          };
        } else {
          next[index] = { ...next[index], codEntradaValido: false };
          toast.error(`Código de entrada "${cod.trim()}" não encontrado nos produtos do emitente`);
        }
        return next;
      });
    } catch {
      toast.error("Erro ao validar código de entrada");
    } finally {
      setValidatingCodEntradaIndex(null);
    }
  };

  // Upload de anexo via drag-and-drop ou seleção de arquivo
  const handleUploadAnexo = async (file: File) => {
    if (!anexosModalEntradaId) return;
    setUploadingAnexo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      await saveAnexoMutation.mutateAsync({
        entradaId: anexosModalEntradaId,
        nome: file.name,
        url: data.url,
        key_arquivo: data.key,
        tamanho: data.size,
        mimeType: data.mimeType,
      });
      toast.success(`Arquivo "${file.name}" anexado!`);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao fazer upload');
    } finally {
      setUploadingAnexo(false);
    }
  };

  // Abrir modal para criar
  const handleOpenCreate = async () => {
    setEditingId(null);
    setIsViewMode(false);
    setFormNome("");
    setFormEmitenteId(isCliente ? (user?.emitenteId ?? null) : null);
    setFormEmitenteNome(isCliente ? (user as any)?.emitenteNome ?? "" : "");
    setFormDataAgendamento(new Date().toISOString().split("T")[0]);
    setFormObservacoes("");
    setFormStatus("agendado");
    setFormItens([emptyItem()]);
    setErrosImportacaoItens([]);
    setIsModalOpen(true);
    if (isCliente && user?.emitenteId && configuracaoUpsellerDoEmitente(user.emitenteId)?.ativo) {
      await abrirUpseller(user.emitenteId);
    }
  };

  // Abrir modal para editar
  const handleOpenEdit = async (entrada: any, viewOnly = false) => {
    setLoadingEdit(true);
    try {
      // Buscar dados completos (com itens) via getById
      const params = encodeURIComponent(JSON.stringify({ json: { id: entrada.id } }));
      const res = await fetch(`/api/trpc/entradas.getById?input=${params}`, { credentials: 'include' });
      const json = await res.json();
      const full = json?.result?.data?.json ?? entrada;
      setEditingId(full.id);
      setIsViewMode(viewOnly);
      setFormNome(full.nome ?? "");
      setFormEmitenteId(full.emitenteId ?? null);
      setFormEmitenteNome(full.emitenteNome ?? "");
      setFormDataAgendamento(full.dataAgendamento ? String(full.dataAgendamento).split("T")[0] : "");
      setFormObservacoes(full.observacoes ?? "");
      setFormStatus(full.status ?? "agendado");
      setErrosImportacaoItens([]);
      const itens: ItemForm[] = (full.itens ?? []).map((item: any) => ({
        produtoCod: item.produtoCod ?? "",
        produtoDescricao: item.produtoDescricao ?? "",
        codigoEntrada: item.codigoEntrada ?? "",
        retrabalhar: !!item.retrabalhar,
        quantidade: String(item.quantidade ?? ""),
        unidade: item.unidade ?? "",
        custoUnitario: String(item.custoUnitario ?? ""),
        custoTotal: String(item.custoTotal ?? ""),
        origemFiscal: String(item.origemFiscal ?? "0"),
        codValido: true,
      }));
      setFormItens(itens.length > 0 ? itens : [emptyItem()]);
      setIsModalOpen(true);
    } catch {
      toast.error("Erro ao carregar dados do agendamento");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setIsViewMode(false);
    setErrosImportacaoItens([]);
  };

  const configuracaoUpsellerDoEmitente = (emitenteId: number | null) =>
    (upsellerConfigs as any[]).find((configuracao) => Number(configuracao.emitenteId) === Number(emitenteId));

  const abrirUpseller = async (emitenteId: number) => {
    setVerificandoUpseller(true);
    try {
      const resposta = await fetch(`/api/upseller/browser/${emitenteId}`, { credentials: "include" });
      const resultado = await resposta.json();
      if (!resposta.ok) throw new Error(resultado?.error || "Não foi possível abrir o UpSeller.");
      setUpsellerAutenticadoEmitenteId(resultado.autenticada ? emitenteId : null);
      if (!resultado.autenticada) {
        setUpsellerLoginEmitenteId(emitenteId);
        setUpsellerLoginUrl(resultado.url);
        toast.error("Faça o login do UpSeller nesta janela antes de continuar.");
      } else {
        setUpsellerLoginUrl(null);
        setUpsellerLoginEmitenteId(null);
        toast.success("Sessão do UpSeller validada.");
      }
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível verificar o UpSeller.");
    } finally {
      setVerificandoUpseller(false);
    }
  };

  const abrirConfiguracaoUpseller = (emitenteId: number | null) => {
    const configuracao = configuracaoUpsellerDoEmitente(emitenteId);
    setUpsellerEmitenteId(emitenteId);
    setUpsellerArmazem(configuracao?.armazemUpseller ?? "");
    setUpsellerAtivo(configuracao ? Boolean(configuracao.ativo) : true);
    setUpsellerConfigOpen(true);
  };

  const selecionarEmitenteDoFormulario = async (emitenteId: number) => {
    setFormEmitenteId(emitenteId);
    const configuracao = configuracaoUpsellerDoEmitente(emitenteId);
    if (configuracao?.ativo) await abrirUpseller(emitenteId);
  };

  // Adicionar item
  const addItem = () => setFormItens((prev) => [...prev, emptyItem()]);

  // Remover item
  const removeItem = (index: number) => {
    setFormItens((prev) => prev.filter((_, i) => i !== index));
  };

  // Atualizar item
  const updateItem = (index: number, field: keyof ItemForm, value: any) => {
    setFormItens((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };

      // Auto-preencher codigoEntrada com produtoCod se retrabalhar = false
      if (field === "retrabalhar" && !value) {
        next[index].codigoEntrada = next[index].produtoCod;
      }
      if (field === "produtoCod" && !next[index].retrabalhar) {
        next[index].codigoEntrada = value;
      }

      // Calcular custo total
      if (field === "quantidade" || field === "custoUnitario") {
        const qty = parseFloat(field === "quantidade" ? value : next[index].quantidade) || 0;
        const unit = parseFloat(field === "custoUnitario" ? value : next[index].custoUnitario) || 0;
        next[index].custoTotal = (qty * unit).toFixed(2);
      }

      return next;
    });
  };

  // Salvar
  const handleSave = async () => {
    if (!formNome.trim()) { toast.error("Nome do agendamento é obrigatório"); return; }
    if (!formEmitenteId) { toast.error("Emitente é obrigatório"); return; }
    if (!formDataAgendamento) { toast.error("Data de agendamento é obrigatória"); return; }

    const itensValidos = formItens.filter((i) => i.produtoCod.trim() && i.codigoEntrada.trim());
    if (itensValidos.length === 0) { toast.error("Adicione pelo menos um item"); return; }

    // Validar que todos os códigos de produto foram verificados e existem no sistema
    const itensInvalidos = itensValidos.filter((i) => i.codValido !== true);
    if (itensInvalidos.length > 0) {
      toast.error(`Os seguintes códigos não foram validados ou não existem: ${itensInvalidos.map(i => i.produtoCod).join(", ")}`);
      return;
    }

    if (!editingId && isCliente && configuracaoUpsellerDoEmitente(formEmitenteId)?.ativo && upsellerAutenticadoEmitenteId !== formEmitenteId) {
      toast.error("Faça o login do UpSeller antes de concluir o agendamento.");
      await abrirUpseller(formEmitenteId);
      return;
    }

    const itensPayload = itensValidos.map((i) => ({
      produtoCod: i.produtoCod.trim(),
      produtoDescricao: i.produtoDescricao.trim() || undefined,
      codigoEntrada: i.codigoEntrada.trim(),
      retrabalhar: i.retrabalhar,
      quantidade: parseFloat(i.quantidade) || 0,
      unidade: i.unidade.trim() || undefined,
      custoUnitario: parseFloat(i.custoUnitario) || 0,
      custoTotal: parseFloat(i.custoTotal) || 0,
      origemFiscal: i.origemFiscal !== "" ? parseInt(i.origemFiscal) : null,
    }));

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, nome: formNome, status: formStatus, dataAgendamento: formDataAgendamento, observacoes: formObservacoes });
      await updateItensMutation.mutateAsync({ entradaId: editingId, itens: itensPayload });
      toast.success("Agendamento atualizado!");
      handleCloseModal();
      refetch();
    } else {
      createMutation.mutate({
        nome: formNome,
        emitenteId: formEmitenteId,
        dataAgendamento: formDataAgendamento,
        observacoes: formObservacoes,
        itens: itensPayload,
      });
    }
  };

  // Exportar XLSX
  const handleExport = () => {
    const data = entradas.map((e: any) => ({
      "Nº Agendamento": e.numeroAgendamento,
      "Nome": e.nome,
      "Emitente": e.emitenteNome,
      "Status": STATUS_LABELS[e.status] ?? e.status,
      "Data Agendamento": e.dataAgendamento ? e.dataAgendamento.split("T")[0] : "",
      "Nota Fiscal": e.notaFiscal ?? "-",
      "Total Itens": e.totalItens ?? 0,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Agendamentos");
    XLSX.writeFile(wb, "agendamentos_entradas.xlsx");
    toast.success("Arquivo exportado com sucesso!");
  };

  // Exportar relatório XLSX (com itens)
  const handleExportRelatorio = () => {
    if (!relatorioData || relatorioData.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }
    const data = relatorioData.map((row: any) => ({
      "Nº Agendamento": row.numeroAgendamento,
      "Nome": row.nome,
      "Emitente": row.emitenteNome,
      "Status": STATUS_LABELS[row.status] ?? row.status,
      "Data Agendamento": row.dataAgendamento ? row.dataAgendamento.split("T")[0] : "",
      "Nota Fiscal": row.notaFiscal ?? "-",
      "Produto Cód": row.produtoCod ?? "",
      "Produto Descrição": row.produtoDescricao ?? "",
      "Cód Entrada": row.codigoEntrada ?? "",
      "Retrabalhar": row.retrabalhar ? "Sim" : "Não",
      "Quantidade": row.quantidade ? parseInt(row.quantidade) : 0,
      "Unidade": row.unidade ?? "",
      "Custo Unitário": row.custoUnitario ?? 0,
      "Custo Total": row.custoTotal ?? 0,
      "Criado Em": row.criadoEm ? new Date(row.criadoEm).toLocaleDateString("pt-BR") : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Relatório Entradas");
    XLSX.writeFile(wb, `relatorio_entradas_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Relatório exportado!");
  };

  // Importar XLSX
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);

        if (json.length === 0) { toast.error("Planilha vazia"); return; }

        // Agrupar por número de agendamento ou nome
        const agrupados: Record<string, any> = {};
        for (const row of json) {
          const chave = (row["Nº Agendamento"] || row["Nome"] || "").toString().trim();
          if (!chave) continue;
          if (!agrupados[chave]) {
            agrupados[chave] = {
              nome: (row["Nome"] || chave).toString().trim(),
              dataAgendamento: row["Data Agendamento"] ? row["Data Agendamento"].toString().trim() : new Date().toISOString().split("T")[0],
              observacoes: row["Observações"] || row["observacoes"] || "",
              itens: [],
            };
          }
          if (row["Produto Cód"] || row["produtoCod"]) {
            agrupados[chave].itens.push({
              produtoCod: (row["Produto Cód"] || row["produtoCod"] || "").toString().trim(),
              produtoDescricao: (row["Produto Descrição"] || row["produtoDescricao"] || "").toString().trim(),
              codigoEntrada: (row["Cód Entrada"] || row["codigoEntrada"] || "").toString().trim(),
              retrabalhar: (row["Retrabalhar"] || "Não").toString().toLowerCase() === "sim",
              quantidade: parseFloat(row["Quantidade"] || row["quantidade"]) || 0,
              unidade: (row["Unidade"] || row["unidade"] || "").toString().trim(),
              custoUnitario: parseFloat(row["Custo Unitário"] || row["custoUnitario"]) || 0,
              custoTotal: parseFloat(row["Custo Total"] || row["custoTotal"]) || 0,
              origemFiscal: row["Origem Fiscal"] !== undefined ? parseInt(row["Origem Fiscal"]) : null,
            });
          }
        }

        const agendamentos = Object.values(agrupados);
        if (agendamentos.length === 0) { toast.error("Nenhum agendamento válido encontrado"); return; }

        let criados = 0;
        const promises = agendamentos.map((ag: any) =>
          createMutation.mutateAsync({
            nome: ag.nome,
            emitenteId: formEmitenteId ?? (isCliente ? user?.emitenteId ?? undefined : undefined),
            dataAgendamento: ag.dataAgendamento,
            observacoes: ag.observacoes || undefined,
            itens: ag.itens,
          }).then(() => { criados++; })
        );

        Promise.all(promises).then(() => {
          toast.success(`${criados} agendamento(s) importado(s) com sucesso!`);
          refetch();
        }).catch(() => toast.error("Erro ao importar alguns agendamentos"));

      } catch (error) {
        toast.error("Erro ao processar arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // Exportar modelo de itens do modal
  const handleExportModeloItens = () => {
    const modelo = [
      {
        "Cód Produto": "EXEMPLO001",
        "Cód Entrada": "EXEMPLO001",
        "Retrabalhar": "Não",
        "Qtd": 10,
        "Custo Unit": 5.00,
        "Origem Fiscal": "0 - Nacional",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(modelo);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Itens");
    XLSX.writeFile(wb, "modelo_itens_agendamento.xlsx");
    toast.success("Modelo exportado!");
  };

  // Importar itens do modal via Excel
  const handleImportItens = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!formEmitenteId) { toast.error("Selecione o emitente antes de importar os itens"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async (event) => {
      setImportandoItens(true);
      setErrosImportacaoItens([]);
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);
        if (json.length === 0) { toast.error("Planilha vazia"); return; }
        const novosItens: ItemForm[] = [];
        const erros: string[] = [];
        const produtosConsultados = new Map<string, any | null>();
        const consultarProduto = async (codigo: string) => {
          if (produtosConsultados.has(codigo)) return produtosConsultados.get(codigo);
          const params = encodeURIComponent(JSON.stringify({ json: { cod: codigo, emitenteId: formEmitenteId } }));
          const resposta = await fetch(`/api/trpc/products.getByCod?input=${params}`, { credentials: "include" });
          if (!resposta.ok) throw new Error("Falha ao validar código");
          const resultado = await resposta.json();
          const produto = resultado?.result?.data?.json ?? null;
          produtosConsultados.set(codigo, produto);
          return produto;
        };
        for (const [indice, row] of json.entries()) {
          const linha = indice + 2;
          const cod = (row["Cód Produto"] || row["produtoCod"] || "").toString().trim().toUpperCase();
          const retrabalhar = (row["Retrabalhar"] || "Não").toString().toLowerCase() === "sim";
          const codigoEntradaInformado = (row["Cód Entrada"] || row["codigoEntrada"] || "").toString().trim().toUpperCase();
          const codEntrada = retrabalhar ? codigoEntradaInformado : (codigoEntradaInformado || cod);
          const origemFiscalTexto = row["Origem Fiscal"] !== undefined ? String(row["Origem Fiscal"]).trim() : "0";
          const origemFiscal = parseInt(origemFiscalTexto, 10);
          if (!cod) { erros.push(`Linha ${linha}: Cód Produto obrigatório.`); continue; }
          if (retrabalhar && !codEntrada) { erros.push(`Linha ${linha}: Cód Entrada obrigatório para item retrabalhado.`); continue; }
          if (!Number.isInteger(origemFiscal) || origemFiscal < 0 || origemFiscal > 7) { erros.push(`Linha ${linha}: Origem Fiscal "${origemFiscalTexto}" inválida.`); continue; }
          let produto: any;
          try { produto = await consultarProduto(cod); } catch { erros.push(`Linha ${linha}: Cód Produto "${cod}": erro ao validar.`); continue; }
          if (!produto) { erros.push(`Linha ${linha}: Cód Produto "${cod}": não encontrado no emitente selecionado.`); continue; }
          if (retrabalhar) {
            let produtoEntrada: any;
            try { produtoEntrada = await consultarProduto(codEntrada); } catch { erros.push(`Linha ${linha}: Cód Entrada "${codEntrada}": erro ao validar.`); continue; }
            if (!produtoEntrada) { erros.push(`Linha ${linha}: Cód Entrada "${codEntrada}": não encontrado no emitente selecionado.`); continue; }
          }
          const quantidade = String(parseFloat(row["Qtd"] || row["quantidade"]) || 0);
          const custoUnitario = String(parseFloat(row["Custo Unit"] || row["custoUnitario"]) || 0);
          const qty = parseFloat(quantidade) || 0;
          const unit = parseFloat(custoUnitario) || 0;
          novosItens.push({ produtoCod: cod, produtoDescricao: produto.descricao ?? "", codigoEntrada: codEntrada, retrabalhar, quantidade, unidade: "", custoUnitario, custoTotal: (qty * unit).toFixed(2), origemFiscal: String(origemFiscal), codValido: true, codEntradaValido: retrabalhar ? true : null });
        }
        if (novosItens.length > 0) setFormItens(novosItens);
        setErrosImportacaoItens(erros);
        if (erros.length > 0) toast.error(`${erros.length} item(ns) não importado(s). Veja os erros no modal.`);
        else toast.success(`${novosItens.length} item(s) importado(s) e validado(s).`);
      } catch {
        toast.error("Erro ao processar arquivo");
      } finally {
        setImportandoItens(false);
        e.target.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agendamento de Entradas</h1>
          <p className="text-sm text-gray-500 mt-1">{total} agendamento(s) encontrado(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => abrirConfiguracaoUpseller(isCliente ? (user?.emitenteId ?? null) : filterEmitenteId)}>
            Configurar UpSeller
          </Button>
          <Button variant="outline" onClick={() => setIsRelatorioOpen(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Relatório
          </Button>

          <Button onClick={handleOpenCreate} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Novo Agendamento
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1 block">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Nome, nº agendamento, nota fiscal ou SKU..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
              </div>
            </div>
            {!isCliente && (
              <div className="w-52">
                <Label className="text-xs text-gray-500 mb-1 block">Emitente</Label>
                <Select
                  value={filterEmitenteId ? String(filterEmitenteId) : "todos"}
                  onValueChange={(v) => { setFilterEmitenteId(v === "todos" ? null : Number(v)); setPage(1); }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {emitentes.map((em: any) => (
                      <SelectItem key={em.id} value={String(em.id)}>{em.razaoSocial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {/* Filtros rápidos de status */}
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              { value: "todos", label: "Todos" },
              { value: "agendado", label: "Agendado" },
              { value: "recebido", label: "Recebido" },
              { value: "em_conferencia", label: "Em Conferência" },
              { value: "conferido", label: "Conferido" },
              { value: "finalizado", label: "Finalizado" },
              { value: "enderecado", label: "Endereçado" },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => { setFilterStatus(s.value); setFilterDataAgendamento("todos"); setPage(1); }}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                  filterStatus === s.value
                    ? "bg-orange-500 text-white border-orange-500 font-medium"
                    : "bg-white text-gray-600 border-gray-300 hover:border-orange-400 hover:text-orange-500"
                }`}
              >
                {s.label}
              </button>
            ))}
            {([
              { value: "atrasado", label: "Atrasados" },
              { value: "hoje", label: "Hoje" },
            ] as const).map((filtro) => (
              <button
                key={filtro.value}
                onClick={() => { setFilterDataAgendamento(filtro.value); setFilterStatus("agendado"); setPage(1); }}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                  filterDataAgendamento === filtro.value
                    ? "bg-orange-500 text-white border-orange-500 font-medium"
                    : "bg-white text-gray-600 border-gray-300 hover:border-orange-400 hover:text-orange-500"
                }`}
              >
                {filtro.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nº Agendamento</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Emitente</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Data Agendamento</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nota Fiscal</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">XML / DANFE</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Anexos</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Itens</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {entradas.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-gray-400">
                      Nenhum agendamento encontrado
                    </td>
                  </tr>
                ) : (
                  entradas.map((entrada: any) => (
                    <tr key={entrada.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">{entrada.numeroAgendamento}</td>
                      <td className="px-4 py-3 font-medium">{entrada.nome}</td>
                      <td className="px-4 py-3 text-gray-600">{entrada.emitenteNome ?? "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[entrada.status] ?? "bg-gray-100 text-gray-700"}`}>
                          {STATUS_LABELS[entrada.status] ?? entrada.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{entrada.dataAgendamento ? new Date(entrada.dataAgendamento.toString().includes("T") ? entrada.dataAgendamento : entrada.dataAgendamento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</span>
                          {(() => {
                            const flag = getFlagDataAgendamento(entrada.dataAgendamento, entrada.status);
                            return flag ? <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${flag.color}`}>{flag.label}</span> : null;
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {entrada.notaFiscal ? (
                          entrada.notaFiscalUrl ? (
                            <a href={entrada.notaFiscalUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                              {entrada.notaFiscal}
                            </a>
                          ) : (
                            <span className="text-xs">{entrada.notaFiscal}</span>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      {/* Coluna XML / DANFE */}
                      <td className="px-4 py-3">
                        {entrada.notaFiscal ? (
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setXmlModalEntradaId(entrada.id)}
                              title="Visualizar / Baixar XML"
                            >
                              <FileCode2 className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDanfeModalEntradaId(entrada.id)}
                              title="Visualizar / Baixar DANFE"
                            >
                              <FileText className="w-4 h-4 text-green-600" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs flex justify-center">—</span>
                        )}
                      </td>

                      {/* Coluna Anexos */}
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAnexosModalEntradaId(entrada.id)}
                            title="Gerenciar Anexos"
                          >
                            <Paperclip className="w-4 h-4 text-gray-500" />
                          </Button>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-600">{entrada.totalItens ?? 0}</td>
                      <td className="px-4 py-3">
                        {(() => {
                          const statusBloqueado = ['conferido', 'finalizado', 'enderecado'].includes(entrada.status);
                          return (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={loadingEdit}
                                onClick={() => handleOpenEdit(entrada, true)}
                                title="Visualizar"
                              >
                                <Eye className={`w-4 h-4 ${loadingEdit ? 'text-gray-300' : 'text-gray-500'}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={statusBloqueado || loadingEdit}
                                onClick={() => {
                                  if (statusBloqueado || loadingEdit) return;
                                  handleOpenEdit(entrada, false);
                                }}
                                title={statusBloqueado ? `Não é possível editar: status é ${STATUS_LABELS[entrada.status] ?? entrada.status}` : "Editar"}
                              >
                                <Pencil className={`w-4 h-4 ${statusBloqueado || loadingEdit ? 'text-gray-300' : 'text-orange-500'}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={statusBloqueado}
                                onClick={() => {
                                  if (statusBloqueado) return;
                                  if (confirm("Excluir este agendamento?")) deleteMutation.mutate({ id: entrada.id });
                                }}
                                title={statusBloqueado ? `Não é possível excluir: status é ${STATUS_LABELS[entrada.status] ?? entrada.status}` : "Excluir"}
                              >
                                <Trash2 className={`w-4 h-4 ${statusBloqueado ? 'text-gray-300' : 'text-red-500'}`} />
                              </Button>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-gray-500">Página {page} de {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent className="max-w-5xl sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isViewMode ? "Visualizar Agendamento" : editingId ? "Editar Agendamento" : "Novo Agendamento"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Dados gerais */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome do Agendamento *</Label>
                <Input
                  value={formNome}
                  onChange={(e) => setFormNome(e.target.value)}
                  disabled={isViewMode}
                  placeholder="Ex: Entrada de mercadoria - Fornecedor X"
                />
              </div>
              <div>
                <Label>Emitente *</Label>
                {isCliente && editingId ? (
                  <Input value={formEmitenteNome || String(formEmitenteId ?? "")} disabled />
                ) : (
                  <Select
                    value={formEmitenteId ? String(formEmitenteId) : ""}
                    onValueChange={(v) => { void selecionarEmitenteDoFormulario(Number(v)); }}
                    disabled={isViewMode}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o emitente" />
                    </SelectTrigger>
                    <SelectContent>
                      {emitentes.map((em: any) => (
                        <SelectItem key={em.id} value={String(em.id)}>{em.razaoSocial}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label>Data de Agendamento *</Label>
                <Input
                  type="date"
                  value={formDataAgendamento}
                  onChange={(e) => setFormDataAgendamento(e.target.value)}
                  disabled={isViewMode}
                />
              </div>
              {editingId && (
                <div>
                  <Label>Status</Label>
                  <Select value={formStatus} onValueChange={setFormStatus} disabled={isViewMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agendado">Agendado</SelectItem>
                      <SelectItem value="recebido">Recebido</SelectItem>
                      <SelectItem value="em_conferencia">Em Conferência</SelectItem>
                      <SelectItem value="conferido">Conferido</SelectItem>
                      <SelectItem value="finalizado">Finalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {formEmitenteId && configuracaoUpsellerDoEmitente(formEmitenteId)?.ativo && !isViewMode && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm">
                <span>UpSeller deste emitente: {upsellerAutenticadoEmitenteId === formEmitenteId ? "sessão validada" : "faça o login antes de finalizar a entrada"}.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => void abrirUpseller(formEmitenteId)} disabled={verificandoUpseller}>
                  {verificandoUpseller ? "Verificando..." : "Abrir login UpSeller"}
                </Button>
              </div>
            )}

            <div>
              <Label>Observações</Label>
              <Textarea
                value={formObservacoes}
                onChange={(e) => setFormObservacoes(e.target.value)}
                disabled={isViewMode}
                rows={2}
                placeholder="Observações opcionais..."
              />
            </div>

            {/* Itens */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Itens</Label>
                {!isViewMode && (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleExportModeloItens}>
                      <Download className="w-3 h-3 mr-1" /> Exportar Modelo
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => modalImportRef.current?.click()} disabled={importandoItens}>
                      <Upload className="w-3 h-3 mr-1" /> {importandoItens ? "Validando itens..." : "Importar Itens"}
                    </Button>
                    <input ref={modalImportRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportItens} />
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="w-3 h-3 mr-1" /> Adicionar Item
                    </Button>
                  </div>
                )}
              </div>

              {errosImportacaoItens.length > 0 && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <p className="font-medium">Itens não importados ({errosImportacaoItens.length})</p>
                  <ul className="mt-1 max-h-32 list-disc overflow-y-auto pl-5">
                    {errosImportacaoItens.map((erro, indice) => <li key={`${erro}-${indice}`}>{erro}</li>)}
                  </ul>
                </div>
              )}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-medium min-w-[110px]">Cód Produto *</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[150px]">Descrição</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[80px]">Retrabalhar</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[110px]">Cód Entrada *</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[80px]">Qtd *</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[100px]">Custo Unit</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[100px]">Custo Total</th>
                      <th className="text-left px-3 py-2 font-medium min-w-[160px]">Origem Fiscal</th>
                      {!isViewMode && <th className="px-3 py-2 min-w-[40px]"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {formItens.map((item, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="px-2 py-1">
                          <Input
                            value={item.produtoCod}
                            onChange={(e) => { updateItem(index, "produtoCod", e.target.value); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleValidateProdutoCod(index, item.produtoCod); } }}
                            onBlur={() => { if (item.produtoCod.trim() && item.codValido === null) handleValidateProdutoCod(index, item.produtoCod); }}
                            disabled={isViewMode || validatingIndex === index}
                            className={`h-7 text-xs ${item.codValido === false ? "border-red-500 focus-visible:ring-red-500" : item.codValido === true ? "border-green-500" : ""}`}
                            placeholder="COD001"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={item.produtoDescricao}
                            readOnly
                            className="h-7 text-xs bg-gray-50 cursor-default"
                            placeholder={validatingIndex === index ? "Buscando..." : "Pressione Enter no cód"}
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          <Select
                            value={item.retrabalhar ? "sim" : "nao"}
                            onValueChange={(v) => updateItem(index, "retrabalhar", v === "sim")}
                            disabled={isViewMode}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nao">Não</SelectItem>
                              <SelectItem value="sim">Sim</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={item.codigoEntrada}
                            onChange={(e) => { updateItem(index, "codigoEntrada", e.target.value); }}
                            onKeyDown={(e) => { if (e.key === "Enter" && item.retrabalhar) { e.preventDefault(); handleValidateCodEntrada(index, item.codigoEntrada); } }}
                            onBlur={() => { if (item.retrabalhar && item.codigoEntrada.trim() && item.codEntradaValido === null) handleValidateCodEntrada(index, item.codigoEntrada); }}
                            disabled={isViewMode || !item.retrabalhar || validatingCodEntradaIndex === index}
                            className={`h-7 text-xs ${item.retrabalhar && item.codEntradaValido === false ? "border-red-500 focus-visible:ring-red-500" : item.retrabalhar && item.codEntradaValido === true ? "border-green-500" : ""}`}
                            placeholder={item.retrabalhar ? (validatingCodEntradaIndex === index ? "Buscando..." : "Pressione Enter no cód") : item.produtoCod}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            value={item.quantidade}
                            onChange={(e) => updateItem(index, "quantidade", e.target.value)}
                            disabled={isViewMode}
                            className="h-7 text-xs"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            value={item.custoUnitario}
                            onChange={(e) => updateItem(index, "custoUnitario", e.target.value)}
                            disabled={isViewMode}
                            className="h-7 text-xs"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            value={item.custoTotal}
                            readOnly
                            className="h-7 text-xs bg-gray-50 cursor-default"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Select
                            value={item.origemFiscal}
                            onValueChange={(v) => updateItem(index, "origemFiscal", v)}
                            disabled={isViewMode}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0 - Nacional</SelectItem>
                              <SelectItem value="1">1 - Estrangeira (import. direta)</SelectItem>
                              <SelectItem value="2">2 - Estrangeira (merc. interno)</SelectItem>
                              <SelectItem value="3">3 - Nacional c/ import. &gt;40%</SelectItem>
                              <SelectItem value="4">4 - Nacional (proc. básicos)</SelectItem>
                              <SelectItem value="5">5 - Nacional c/ import. ≤40%</SelectItem>
                              <SelectItem value="6">6 - Estrang. (import. direta s/ similar)</SelectItem>
                              <SelectItem value="7">7 - Estrang. (merc. interno s/ similar)</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        {!isViewMode && (
                          <td className="px-2 py-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => removeItem(index)}
                              disabled={formItens.length === 1}
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!isViewMode && (
                <p className="text-xs text-gray-400 mt-1">
                  * Quando "Retrabalhar = Não", o Cód Entrada é preenchido automaticamente com o Cód Produto. O código deve existir nos produtos do emitente.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseModal}>
              {isViewMode ? "Fechar" : "Cancelar"}
            </Button>
            {!isViewMode && (
              <Button
                onClick={handleSave}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Relatório */}
      <Dialog open={isRelatorioOpen} onOpenChange={setIsRelatorioOpen}>
        <DialogContent className="max-w-lg sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Relatório de Entradas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Início</Label>
                <Input type="date" value={relDataInicio} onChange={(e) => setRelDataInicio(e.target.value)} />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input type="date" value={relDataFim} onChange={(e) => setRelDataFim(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={relStatus} onValueChange={setRelStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="agendado">Agendado</SelectItem>
                  <SelectItem value="recebido">Recebido</SelectItem>
                  <SelectItem value="em_conferencia">Em Conferência</SelectItem>
                  <SelectItem value="conferido">Conferido</SelectItem>
                  <SelectItem value="finalizado">Finalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isCliente && (
              <div>
                <Label>Emitente</Label>
                <Select
                  value={filterEmitenteId ? String(filterEmitenteId) : "todos"}
                  onValueChange={(v) => setFilterEmitenteId(v === "todos" ? null : Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {emitentes.map((em: any) => (
                      <SelectItem key={em.id} value={String(em.id)}>{em.razaoSocial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-sm text-gray-500">
              {relatorioData ? `${relatorioData.length} linha(s) no relatório` : "Carregando..."}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsRelatorioOpen(false)}>Fechar</Button>
            <Button onClick={handleExportRelatorio} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Download className="w-4 h-4 mr-2" />
              Exportar XLSX
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={upsellerConfigOpen} onOpenChange={setUpsellerConfigOpen}>
        <DialogContent className="max-w-lg sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurar UpSeller</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Emitente *</Label>
              <Select value={upsellerEmitenteId ? String(upsellerEmitenteId) : ""} onValueChange={(valor) => abrirConfiguracaoUpseller(Number(valor))}>
                <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                <SelectContent>
                  {emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Armazém no UpSeller *</Label>
              <Input value={upsellerArmazem} onChange={(event) => setUpsellerArmazem(event.target.value)} placeholder="Nome exibido no UpSeller" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={upsellerAtivo} onChange={(event) => setUpsellerAtivo(event.target.checked)} />
              Ativar lançamento automático no UpSeller
            </label>
            <p className="text-xs text-muted-foreground">O login é feito pelo usuário na janela do UpSeller; senha, CAPTCHA e código por e-mail não ficam no Box.</p>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setUpsellerConfigOpen(false)}>Cancelar</Button>
            <Button
              disabled={!upsellerEmitenteId || !upsellerArmazem.trim() || salvarUpsellerConfigMutation.isPending}
              onClick={() => salvarUpsellerConfigMutation.mutate({ emitenteId: upsellerEmitenteId!, armazemUpseller: upsellerArmazem, ativo: upsellerAtivo })}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {salvarUpsellerConfigMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
            <Button type="button" variant="outline" disabled={!upsellerEmitenteId || verificandoUpseller} onClick={() => upsellerEmitenteId && void abrirUpseller(upsellerEmitenteId)}>
              {verificandoUpseller ? "Abrindo..." : "Abrir login UpSeller"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={upsellerLoginUrl !== null} onOpenChange={(open) => { if (!open) { setUpsellerLoginUrl(null); setUpsellerLoginEmitenteId(null); } }}>
        <DialogContent className="!max-w-[95vw] w-[1440px] h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Login do UpSeller</DialogTitle>
          </DialogHeader>
          {upsellerLoginUrl && <iframe title="Login do UpSeller" src={upsellerLoginUrl} className="w-full h-[calc(90vh-73px)] border-0" />}
          <div className="absolute bottom-4 right-4">
            <Button type="button" onClick={() => upsellerLoginEmitenteId && void abrirUpseller(upsellerLoginEmitenteId)} disabled={verificandoUpseller} className="bg-orange-500 hover:bg-orange-600 text-white">
              {verificandoUpseller ? "Verificando..." : "Concluir login"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal XML */}
      <Dialog open={xmlModalEntradaId !== null} onOpenChange={(open) => { if (!open) setXmlModalEntradaId(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>XML da NF-e</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {xmlLoading && <p className="text-gray-500 text-sm">Carregando XML...</p>}
            {xmlError && (
              <p className="text-red-500 text-sm">{(xmlError as any)?.message || 'XML não disponível para esta entrada.'}</p>
            )}
            {xmlData && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const blob = new Blob([xmlData.xml], { type: 'application/xml' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${xmlData.chaveNfe}.xml`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" /> Baixar XML
                  </Button>
                </div>
                <pre className="bg-gray-50 border rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-96">{xmlData.xml}</pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal DANFE */}
      <Dialog open={danfeModalEntradaId !== null} onOpenChange={(open) => { if (!open) setDanfeModalEntradaId(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>DANFE</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {danfeLoading && <p className="text-gray-500 text-sm">Carregando DANFE...</p>}
            {danfeError && (
              <p className="text-red-500 text-sm">{(danfeError as any)?.message || 'DANFE não disponível para esta entrada.'}</p>
            )}
            {danfeData && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const byteCharacters = atob(danfeData.pdf);
                      const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
                      const byteArray = new Uint8Array(byteNumbers);
                      const blob = new Blob([byteArray], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `DANFE_${danfeData.chaveNfe}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" /> Baixar DANFE
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const byteCharacters = atob(danfeData.pdf);
                      const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
                      const byteArray = new Uint8Array(byteNumbers);
                      const blob = new Blob([byteArray], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" /> Visualizar
                  </Button>
                </div>
                <iframe
                  src={`data:application/pdf;base64,${danfeData.pdf}`}
                  className="w-full h-[60vh] border rounded"
                  title="DANFE"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Anexos */}
      <Dialog open={anexosModalEntradaId !== null} onOpenChange={(open) => { if (!open) setAnexosModalEntradaId(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anexos do Recebimento</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            {/* Área de drag-and-drop */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingOver(false);
                const files = Array.from(e.dataTransfer.files);
                files.forEach(handleUploadAnexo);
              }}
            >
              {uploadingAnexo ? (
                <p className="text-blue-600 text-sm">Enviando arquivo...</p>
              ) : (
                <>
                  <Paperclip className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Arraste arquivos aqui ou</p>
                  <label className="mt-2 inline-block cursor-pointer">
                    <span className="text-blue-600 hover:underline text-sm">clique para selecionar</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        files.forEach(handleUploadAnexo);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </>
              )}
            </div>

            {/* Lista de anexos */}
            {(anexosData as any[]).length === 0 ? (
              <p className="text-gray-400 text-sm text-center">Nenhum anexo ainda.</p>
            ) : (
              <div className="space-y-2">
                {(anexosData as any[]).map((anexo: any) => (
                  <div key={anexo.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <a
                          href={anexo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline truncate block"
                        >
                          {anexo.nome}
                        </a>
                        <p className="text-xs text-gray-400">
                          {(anexo.tamanho / 1024).toFixed(1)} KB · {new Date(anexo.criadoEm).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Remover o anexo "${anexo.nome}"?`)) {
                          deleteAnexoMutation.mutate({ id: anexo.id });
                        }
                      }}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
